'use client';

import React, { useMemo } from 'react';
import { Box, IconButton, Typography, type SxProps, type Theme } from '@mui/material';
import { ChevronLeftIcon, ChevronRightIcon } from '@/shared/ui/display/EmojiIcon';
import dayjs, { type Dayjs } from 'dayjs';
import 'dayjs/locale/ko';

dayjs.locale('ko');

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

export interface CalendarDayMeta {
  inMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
  isMobile: boolean;
}

export interface CalendarProps {
  /** 현재 표시 월 (YYYY-MM) */
  month: string;
  /** 선택된 날짜 (YYYY-MM-DD) */
  value: string | null;
  /** 날짜 선택 시 */
  onChange: (date: string) => void;
  /** 월 변경 시 */
  onMonthChange?: (month: string) => void;
  /** 헤더 숨김 */
  hideHeader?: boolean;
  /** 모바일 축약 표시 */
  isMobile?: boolean;
  /**
   * 헤더 월 이름 오른쪽 영역.
   * ex) "총 46건 · 오늘 이전 3건"
   */
  renderHeaderDescription?: () => React.ReactNode;
  /**
   * 날짜 숫자 아래 셀별 추가 콘텐츠.
   * inMonth=false인 날짜는 호출되지 않음.
   */
  renderDayContent?: (date: string, meta: CalendarDayMeta) => React.ReactNode;
  /**
   * 날짜 클릭 시 (onChange 호출 후 실행).
   * Popover anchor 등 추가 핸들링이 필요할 때 사용.
   */
  onDayClick?: (date: string, event: React.MouseEvent<HTMLButtonElement>) => void;
  sx?: SxProps<Theme>;
}

/**
 * 제네릭 월 캘린더.
 *
 * 그리드·네비게이션·날짜 선택만 담당하며 도메인 의존성이 없습니다.
 * - renderHeaderDescription: 월 이름 옆 요약 텍스트
 * - renderDayContent: 날짜 숫자 아래 셀 콘텐츠
 */
export function Calendar({
  month,
  value,
  onChange,
  onMonthChange,
  hideHeader = false,
  isMobile = false,
  renderHeaderDescription,
  renderDayContent,
  onDayClick,
  sx,
}: CalendarProps) {
  const { weeks, displayMonth, m } = useMemo(() => {
    const m = dayjs(month);
    const start = m.startOf('month').startOf('week');
    const weeks: Dayjs[][] = [];
    let cursor = start;

    for (let w = 0; w < 6; w++) {
      const row: Dayjs[] = [];
      for (let d = 0; d < 7; d++) {
        row.push(cursor);
        cursor = cursor.add(1, 'day');
      }
      weeks.push(row);
    }
    return { weeks, displayMonth: m.format('YYYY년 M월'), m };
  }, [month]);

  const today = dayjs().format('YYYY-MM-DD');
  const currentMonthIndex = dayjs(month).month();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        minWidth: 320,
        maxWidth: '100%',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        ...sx,
      }}
    >
      {/* 헤더: 월 이름 + description + 네비게이션 */}
      {!hideHeader && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1.5,
            py: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, color: 'text.primary', flexShrink: 0 }}
            >
              {displayMonth}
            </Typography>
            {renderHeaderDescription?.()}
          </Box>

          {onMonthChange && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
              <IconButton
                size="small"
                onClick={() => onMonthChange(m.subtract(1, 'month').format('YYYY-MM'))}
                aria-label="이전 달"
                sx={{ p: 0.25 }}
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              <Typography
                component="button"
                variant="caption"
                onClick={() => onMonthChange(dayjs().format('YYYY-MM'))}
                sx={{
                  border: 'none',
                  bgcolor: 'transparent',
                  cursor: 'pointer',
                  color: 'text.secondary',
                  px: 0.5,
                  '&:hover': { color: 'text.primary' },
                }}
              >
                오늘
              </Typography>
              <IconButton
                size="small"
                onClick={() => onMonthChange(m.add(1, 'month').format('YYYY-MM'))}
                aria-label="다음 달"
                sx={{ p: 0.25 }}
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>
      )}

      {/* 요일 헤더 */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'action.hover',
        }}
      >
        {WEEKDAYS.map((wd) => (
          <Box
            key={wd}
            sx={{
              py: 0.75,
              textAlign: 'center',
              fontSize: isMobile ? '0.7rem' : '0.75rem',
              color: 'text.secondary',
              fontWeight: 500,
            }}
          >
            {wd}
          </Box>
        ))}
      </Box>

      {/* 날짜 그리드 */}
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {weeks.map((row, wi) => (
          <Box
            key={wi}
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              minHeight: isMobile ? 56 : 72,
            }}
          >
            {row.map((day, di) => {
              const dateKey = day.format('YYYY-MM-DD');
              const inMonth = day.month() === currentMonthIndex;
              const isToday = today === dateKey;
              const isSelected = value === dateKey;

              if (!inMonth) {
                return (
                  <Box
                    key={dateKey}
                    sx={{
                      p: 0.75,
                      borderRight: di < 6 ? '1px solid' : 'none',
                      borderBottom: wi < 5 ? '1px solid' : 'none',
                      borderColor: 'divider',
                      color: 'text.disabled',
                      fontSize: isMobile ? '0.8rem' : '0.875rem',
                    }}
                  >
                    {day.date()}
                  </Box>
                );
              }

              return (
                <Box
                  key={dateKey}
                  component="button"
                  type="button"
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    onChange(dateKey);
                    onDayClick?.(dateKey, e);
                  }}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    justifyContent: 'flex-start',
                    p: 0.75,
                    border: 'none',
                    borderRight: di < 6 ? '1px solid' : 'none',
                    borderBottom: wi < 5 ? '1px solid' : 'none',
                    borderColor: 'divider',
                    bgcolor: 'transparent',
                    cursor: 'pointer',
                    textAlign: 'left',
                    minWidth: 0,
                    fontFamily: 'inherit',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  {/* 날짜 숫자 */}
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      fontSize: isMobile ? '0.8rem' : '0.875rem',
                      fontWeight: isToday ? 600 : 400,
                      color: isSelected ? 'primary.contrastText' : 'text.primary',
                      bgcolor: isSelected ? 'primary.main' : 'transparent',
                      border: isToday && !isSelected ? '1px solid' : 'none',
                      borderColor: 'divider',
                      flexShrink: 0,
                    }}
                  >
                    {day.date()}
                  </Box>

                  {/* 셀 콘텐츠 (소비자 주입) */}
                  {renderDayContent && (
                    <Box sx={{ mt: 0.5, flex: 1, minHeight: 0, overflow: 'hidden', width: '100%' }}>
                      {renderDayContent(dateKey, { inMonth, isToday, isSelected, isMobile })}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    </Box>
  );
}
